import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMesero } from "@/modules/mesero/MeseroProvider";
import { MeseroFrame } from "@/modules/mesero/components/MeseroFrame";
import { MeseroBottomNav } from "@/modules/mesero/components/MeseroBottomNav";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import type { Order, Table } from "@/lib/pos-types";
import { cn, toMs } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";
import { CalendarDays, LogOut, Table2 } from "lucide-react";
import { ZoneTableEditor } from "@/components/pos/zone-table-editor";

export default function MesasPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const {
    currentUser,
    tables,
    orders,
    reservations,
    zones,
    getUnreadAlertsCount,
    openTable,
    unlockTableByCode,
    apiEnabled,
    apiOnline,
    offlineQueueCount,
  } = useMesero();
  const unreadCount = getUnreadAlertsCount();

  const scheduledReservationsCount = useMemo(() => {
    return (reservations ?? []).filter((r) => r.status === "scheduled" && !r.tableId).length;
  }, [reservations]);

  const zonesList = useMemo(() => {
    const ordered = (zones ?? []).filter((z) => z && z.trim().length > 0);
    const fromTables = Array.from(
      new Set(
        tables.map((t) => (t.zone ?? "").trim()).filter((z) => z.length > 0),
      ),
    );
    const missing = fromTables
      .filter((z) => !ordered.includes(z))
      .sort((a, b) => a.localeCompare(b));
    return [...ordered, ...missing];
  }, [tables, zones]);

  const [zoneFilter, setZoneFilter] = useState<string>("todas");
  const [filter, setFilter] = useState<"todas" | "mis" | "listas" | "libres">(
    "todas",
  );
  const [showLayout, setShowLayout] = useState<boolean>(false);
  const [openTableId, setOpenTableId] = useState<string | null>(null);
  const [dinersText, setDinersText] = useState("");
  const [unlockTableCandidate, setUnlockTableCandidate] =
    useState<Table | null>(null);
  const [unlockCodeText, setUnlockCodeText] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const selectedTable = useMemo(
    () =>
      openTableId ? (tables.find((t) => t.id === openTableId) ?? null) : null,
    [openTableId, tables],
  );

  const tableState = useCallbackMesaState({ orders });

  useEffect(() => {
    if (zoneFilter === "todas") return;
    if (zonesList.includes(zoneFilter)) return;
    setZoneFilter("todas");
  }, [zoneFilter, zonesList]);

  const filteredTables = useMemo(() => {
    return tables
      .slice()
      .sort((a, b) => a.number - b.number)
      .filter((t) => {
        if (zoneFilter !== "todas" && t.zone !== zoneFilter) return false;
        const state = tableState(t);
        if (filter === "todas") return true;
        if (filter === "libres") return state === "disponible";
        if (filter === "listas") return state === "lista";
        if (filter === "mis")
          return (
            t.waiter === currentUser.name &&
            state !== "disponible" &&
            state !== "bloqueada" &&
            state !== "reservada"
          );
        return true;
      });
  }, [currentUser.name, filter, tableState, tables, zoneFilter]);

  const { countMis, countListas, countCocina, countLibres } = useMemo(() => {
    const scoped =
      zoneFilter === "todas"
        ? tables
        : tables.filter((t) => t.zone === zoneFilter);
    const libres = scoped.filter((t) => tableState(t) === "disponible").length;
    const mis = scoped.filter(
      (t) =>
        t.waiter === currentUser.name &&
        !["disponible", "bloqueada", "reservada"].includes(tableState(t)),
    ).length;
    const listas = scoped.filter(
      (t) => t.waiter === currentUser.name && tableState(t) === "lista",
    ).length;
    const cocina = scoped.filter(
      (t) => t.waiter === currentUser.name && tableState(t) === "cocina",
    ).length;
    return {
      countMis: mis,
      countListas: listas,
      countCocina: cocina,
      countLibres: libres,
    };
  }, [currentUser.name, tableState, tables, zoneFilter]);

  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const handleTableTap = (t: Table) => {
    const state = tableState(t);
    if (state === "bloqueada") {
      setUnlockTableCandidate(t);
      setUnlockCodeText("");
      return;
    }
    if (state === "reservada") {
      setUnlockTableCandidate(t);
      setUnlockCodeText("");
      return;
    }
    if (state === "disponible") {
      setOpenTableId(t.id);
      setDinersText("");
      return;
    }
    /* if (t.waiter && t.waiter !== currentUser.name) {
      toast.warning("Mesa ocupada", {
        description: `Atendida por ${t.waiter}`,
      });
      return;
    } */
    navigate(`/mesero/mesa/${t.id}/items`);
  };

  const confirmOpenTable = async () => {
    if (!selectedTable) return;
    const raw = dinersText.trim();
    const dinersNumber = raw ? Number(raw) : NaN;
    const diners =
      Number.isFinite(dinersNumber) && dinersNumber > 0
        ? Math.floor(dinersNumber)
        : undefined;
    const ok = await openTable(selectedTable.id, diners);
    if (ok) {
      setOpenTableId(null);
      setDinersText("");
      navigate(`/mesero/comanda/${selectedTable.id}`);
    }
  };

  const confirmUnlockTable = async () => {
    if (!unlockTableCandidate) return;
    if (!apiEnabled) {
      toast.error("Disponible sólo con API");
      return;
    }
    const tableId = unlockTableCandidate.id;
    const code = unlockCodeText.trim();
    if (code.length < 4) {
      toast.error("Código inválido");
      return;
    }
    const isReservationBlocked =
      (unlockTableCandidate.blockedReason ?? "").startsWith("Reservada") ||
      tableState(unlockTableCandidate) === "reservada";
    setUnlocking(true);
    await unlockTableByCode(
      tableId,
      code,
      isReservationBlocked ? "reservation" : "general",
    );
    setUnlocking(false);
    setUnlockTableCandidate(null);
    setUnlockCodeText("");
    setOpenTableId(tableId);
    setDinersText("");
  };

  return (
    <MeseroFrame>
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="px-5 pt-3 pb-2 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[22px] font-semibold text-[#f0f0f0] leading-tight">
              Mis mesas
            </div>
            <div className="text-[12px] text-[#9a9aa8] mt-0.5">
              Turno de {currentUser.name}
              {apiEnabled && !apiOnline && (
                <span className="ml-2 text-[#f5a623]">Sin conexión</span>
              )}
              {offlineQueueCount > 0 && (
                <span className="ml-2 text-[#f5a623]">
                  ({offlineQueueCount})
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                setShowLayout(!showLayout);
              }}
              title="Ver layout de mesas"
              className="w-10 h-10 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center"
            >
              <Table2
                className={`h-4 w-4 ${
                  showLayout ? "text-[#00e5a0]" : "text-[#f0f0f0]"
                }`}
              />
            </button>

            <button
              type="button"
              onClick={() => navigate("/mesero/reservas")}
              title="Ver reservas"
              className="w-10 h-10 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center relative"
            >
              <CalendarDays className="h-4 w-4 text-[#f0f0f0]" />
              {scheduledReservationsCount > 0 && (
                <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#4d9fff] rounded-full text-[9px] font-semibold text-black flex items-center justify-center border-2 border-[#0f0f11]">
                  {Math.min(scheduledReservationsCount, 99)}
                </div>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate("/mesero/alertas")}
              className="w-10 h-10 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center relative"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-[18px] h-[18px]"
                fill="none"
                stroke="#f0f0f0"
                strokeWidth="1.5"
              >
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#00e5a0] rounded-full text-[9px] font-semibold text-black flex items-center justify-center border-2 border-[#0f0f11]">
                  {Math.min(unreadCount, 9)}
                </div>
              )}
            </button>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
              title="Cerrar sesión"
              className="w-10 h-10 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center"
            >
              <LogOut className="h-4 w-4 text-[#f0f0f0]" />
            </button>
          </div>
        </div>

        {showLayout ? (
          <ZoneTableEditor
            tables={tables}
            readonly={true}
            zoneItems={zonesList.map((z, idx) => ({ id: z, name: z, order: idx, layoutWidth: 620, layoutHeight: 420 }))}
            zones={zones}
          />
        ) : (
          <>
            <div className="px-5 pb-1 shrink-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#5a5a6a] px-1">
                Zonas
              </div>
              <div className="flex gap-1.5 pt-2 overflow-x-auto no-scrollbar">
                <ZonePill
                  label="Todas"
                  active={zoneFilter === "todas"}
                  onClick={() => setZoneFilter("todas")}
                />
                {zonesList.map((z) => (
                  <ZonePill
                    key={z}
                    label={z}
                    active={zoneFilter === z}
                    onClick={() => setZoneFilter(z)}
                  />
                ))}
              </div>
            </div>

            <div className="px-5 pb-3 shrink-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#5a5a6a] px-1">
                Estados
              </div>
              <div className="flex gap-1.5 pt-2 overflow-x-auto no-scrollbar">
                <FilterPill
                  label="Todas"
                  active={filter === "todas"}
                  onClick={() => setFilter("todas")}
                />
                <FilterPill
                  label="Mis mesas"
                  active={filter === "mis"}
                  onClick={() => setFilter("mis")}
                />
                <FilterPill
                  label="Listas"
                  active={filter === "listas"}
                  onClick={() => setFilter("listas")}
                />
                <FilterPill
                  label="Libres"
                  active={filter === "libres"}
                  onClick={() => setFilter("libres")}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5">
                {filteredTables.map((t) => {
                  const state = tableState(t);
                  const order = t.currentOrderId
                    ? (orders.find((o) => o.id === t.currentOrderId) ?? orders.find((o) => o.tableId === t.id && o.status !== "closed" && o.status !== "paid") ?? null)
                    : (orders.find((o) => o.tableId === t.id && !o.reservationId && o.status !== "closed" && o.status !== "paid") ?? null);
                  const diners = order?.diners;
                  const elapsed = formatElapsed(toMs(t.openedAt), nowTick);
                  const statusLabel =
                    state === "disponible"
                      ? "DISPONIBLE"
                      : state === "tomando"
                        ? "TOMANDO PEDIDO"
                        : state === "cocina"
                          ? "EN COCINA"
                          : state === "lista"
                            ? "LISTA PARA RECOGER"
                            : state === "entregada"
                              ? "ENTREGADA"
                              : state === "cobro"
                                ? "PENDIENTE DE PAGO"
                                : state === "reservada"
                                  ? "RESERVADA"
                                  : "BLOQUEADA";

                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => handleTableTap(t)}
                      className={cn(
                        "rounded-2xl px-3 py-3 border relative overflow-hidden flex flex-col gap-1 min-h-[80px] sm:min-h-[90px] text-left transition-colors",
                        state === "disponible" &&
                          "bg-[#1e1e23] border-white/10 active:bg-[#26262d]",
                        state === "tomando" &&
                          "bg-[rgba(77,159,255,0.14)] border-[rgba(77,159,255,0.30)]",
                        state === "cocina" &&
                          "bg-[rgba(245,166,35,0.10)] border-[rgba(245,166,35,0.30)]",
                        state === "lista" &&
                          "bg-[rgba(0,229,160,0.12)] border-[rgba(0,229,160,0.30)]",
                        state === "entregada" &&
                          "bg-[rgba(167,139,250,0.10)] border-[rgba(167,139,250,0.30)]",
                        state === "cobro" &&
                          "bg-[rgba(216,90,48,0.10)] border-[rgba(216,90,48,0.30)]",
                        state === "bloqueada" &&
                          "bg-[rgba(255,255,255,0.03)] border-white/10 active:bg-[rgba(255,255,255,0.06)]",
                        state === "reservada" &&
                          "bg-[rgba(212,83,126,0.10)] border-[rgba(212,83,126,0.30)]",
                      )}
                    >
                      <div className="text-[15px] font-semibold text-[#f0f0f0] leading-none flex flex-wrap gap-x-1 gap-y-0.5 items-baseline">
                        <span>Mesa {t.number}</span>
                        <span className="text-[10px] font-normal text-[#9a9aa8]">({t.zone})</span>
                      </div>
                      <div
                        className={cn(
                          "text-[10px] font-medium uppercase tracking-[0.05em]",
                          state === "disponible" && "text-[#5a5a6a]",
                          state === "tomando" && "text-[#4d9fff]",
                          state === "cocina" && "text-[#f5a623]",
                          state === "lista" && "text-[#00e5a0]",
                          state === "entregada" && "text-[#a78bfa]",
                          state === "cobro" && "text-[#d85a30]",
                          state === "bloqueada" && "text-[#5a5a6a]",
                          state === "reservada" && "text-[#d4537e]",
                        )}
                      >
                        {statusLabel}
                      </div>
                      <div className="text-[11px] text-[#9a9aa8]">
                        {state === "disponible"
                          ? `Cap ${t.capacity}`
                          : diners
                            ? `${diners} personas`
                            : t.waiter
                              ? t.waiter
                              : `Cap ${t.capacity}`}
                      </div>
                      {elapsed && (
                        <div className="font-mono text-[11px] text-[#9a9aa8]">
                          {elapsed}
                        </div>
                      )}
                      {(state === "lista" ||
                        state === "cocina" ||
                        state === "cobro") && (
                        <div
                          className={cn(
                            "absolute top-2 right-2 w-2 h-2 rounded-full",
                            state === "lista" && "bg-[#00e5a0]",
                            state === "cocina" && "bg-[#f5a623]",
                            state === "cobro" && "bg-[#d85a30]",
                          )}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-white/10 bg-[#0f0f11] px-5 py-2.5 flex shrink-0">
              <StatItem label="Mis mesas" value={countMis} />
              <StatItem
                label="Lista"
                value={countListas}
                valueClassName="text-[#f5a623]"
              />
              <StatItem
                label="En cocina"
                value={countCocina}
                valueClassName="text-[#00e5a0]"
              />
              <StatItem label="Libres" value={countLibres} />
            </div>

            <MeseroBottomNav alertsCount={unreadCount} />
          </>
        )}
      </div>

      <Drawer
        open={Boolean(openTableId)}
        onOpenChange={(open) => !open && setOpenTableId(null)}
      >
        <DrawerContent className="bg-[#1e1e23] border-t border-white/10 text-[#f0f0f0]">
          <div className="mx-auto mt-4 h-1 w-10 rounded-full bg-white/15" />
          <DrawerHeader className="text-left px-5">
            <DrawerTitle className="text-[#f0f0f0] text-base font-semibold">
              {selectedTable
                ? `Abrir mesa ${selectedTable.number} (${selectedTable.zone})`
                : "Abrir mesa"}
            </DrawerTitle>
            <DrawerDescription className="text-[#9a9aa8] text-xs">
              {selectedTable
                ? `Capacidad máxima: ${selectedTable.capacity}. Comensales (opcional).`
                : "Comensales (opcional)."}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-5 pb-2">
            <Input
              value={dinersText}
              onChange={(e) => setDinersText(e.target.value)}
              inputMode="numeric"
              placeholder="Ej: 4"
              className="bg-[#26262d] border-white/10 text-[#f0f0f0] placeholder:text-[#5a5a6a]"
            />
          </div>
          <DrawerFooter className="px-5 pb-8">
            <div className="flex gap-2">
              <DrawerClose asChild>
                <button
                  type="button"
                  className="flex-1 py-3 rounded-xl border border-white/10 bg-[#26262d] text-[#f0f0f0] text-sm font-medium"
                >
                  Cancelar
                </button>
              </DrawerClose>
              <button
                type="button"
                onClick={confirmOpenTable}
                className="flex-1 py-3 rounded-xl bg-[#00e5a0] text-black text-sm font-semibold disabled:bg-[#26262d] disabled:text-[#5a5a6a]"
                disabled={!selectedTable}
              >
                Abrir y tomar pedido
              </button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={Boolean(unlockTableCandidate)}
        onOpenChange={(open) => {
          if (!open) {
            setUnlockTableCandidate(null);
            setUnlockCodeText("");
          }
        }}
      >
        <DrawerContent className="bg-[#1e1e23] border-t border-white/10 text-[#f0f0f0]">
          <div className="mx-auto mt-4 h-1 w-10 rounded-full bg-white/15" />
          <DrawerHeader className="text-left px-5">
            <DrawerTitle className="text-[#f0f0f0] text-base font-semibold">
              {unlockTableCandidate
                ? `Mesa ${unlockTableCandidate.number} (${unlockTableCandidate.zone})`
                : "Mesa"}
            </DrawerTitle>
            <DrawerDescription className="text-[#9a9aa8] text-xs">
              {unlockTableCandidate?.blockedReason ||
                "Ingresa el código para tomar la mesa."}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-5 pb-2">
            <Input
              value={unlockCodeText}
              onChange={(e) => setUnlockCodeText(e.target.value)}
              type="password"
              inputMode="numeric"
              placeholder={
                (unlockTableCandidate?.blockedReason ?? "").startsWith(
                  "Reservada",
                )
                  ? "Código de reserva"
                  : "Código admin"
              }
              className="bg-[#26262d] border-white/10 text-[#f0f0f0] placeholder:text-[#5a5a6a]"
            />
          </div>
          <DrawerFooter className="px-5 pb-8">
            <div className="flex gap-2">
              <DrawerClose asChild>
                <button
                  type="button"
                  className="flex-1 py-3 rounded-xl border border-white/10 bg-[#26262d] text-[#f0f0f0] text-sm font-medium"
                >
                  Cancelar
                </button>
              </DrawerClose>
              <button
                type="button"
                onClick={confirmUnlockTable}
                className="flex-1 py-3 rounded-xl bg-[#00e5a0] text-black text-sm font-semibold disabled:bg-[#26262d] disabled:text-[#5a5a6a]"
                disabled={!unlockTableCandidate || unlocking}
              >
                {unlocking ? "Validando..." : "Tomar mesa"}
              </button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </MeseroFrame>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3.5 py-1.5 rounded-full text-[12px] font-medium border whitespace-nowrap transition-colors",
        active
          ? "bg-[rgba(0,229,160,0.12)] border-[#00e5a0] text-[#00e5a0]"
          : "bg-[#1e1e23] border-white/10 text-[#9a9aa8]",
      )}
    >
      {label}
    </button>
  );
}

function ZonePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3.5 py-1.5 rounded-full text-[12px] font-medium border whitespace-nowrap transition-colors",
        active
          ? "bg-[rgba(77,159,255,0.14)] border-[#4d9fff] text-[#4d9fff]"
          : "bg-[#1e1e23] border-white/10 text-[#9a9aa8]",
      )}
    >
      {label}
    </button>
  );
}

function StatItem({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: number;
  valueClassName?: string;
}) {
  return (
    <div className="flex-1 text-center">
      <div
        className={cn(
          "text-[18px] font-semibold text-[#f0f0f0] leading-none",
          valueClassName,
        )}
      >
        {value}
      </div>
      <div className="text-[10px] text-[#5a5a6a] mt-0.5">{label}</div>
    </div>
  );
}

function useCallbackMesaState({ orders }: { orders: Order[] }) {
  return useMemo(() => {
    const orderById = new Map(orders.map((o) => [o.id, o] as const));
    const bestActiveByTableId = new Map<string, Order>();
    for (const o of orders) {
      if (o.status === "closed") continue;
      if (!o.tableId) continue;
      const prev = bestActiveByTableId.get(o.tableId);
      if (!prev) {
        bestActiveByTableId.set(o.tableId, o);
        continue;
      }
      if (
        toMs(o.updatedAt) > toMs(prev.updatedAt)
      ) {
        bestActiveByTableId.set(o.tableId, o);
      }
    }
    return (
      t: Table,
    ):
      | "disponible"
      | "tomando"
      | "cocina"
      | "lista"
      | "entregada"
      | "cobro"
      | "bloqueada"
      | "reservada" => {
      if (t.status === "blocked") return "bloqueada";
      if (t.status === "available") return "disponible";
      if ((t as unknown as { status?: string }).status === "reserved")
        return "reservada";
      const order =
        (t.currentOrderId ? orderById.get(t.currentOrderId) : null) ??
        bestActiveByTableId.get(t.id) ??
        null;
      const orderStatus = order?.status;
      if (t.status === "pending-payment" || orderStatus === "pending-payment")
        return "cobro";
      if (orderStatus === "ready") return "lista";
      if (orderStatus === "in-kitchen") return "cocina";
      if (orderStatus === "delivered") return "entregada";
      if (orderStatus === "open") return "tomando";
      if (!order || orderStatus === "closed" || orderStatus === "paid") return "disponible";
      return "disponible";
    };
  }, [orders]);
}

function formatElapsed(openedAtMs: number | undefined, nowMs: number) {
  if (!openedAtMs) return null;
  const diff = Math.max(0, nowMs - openedAtMs);
  const minutes = Math.floor(diff / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1_000);
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return `${mm}:${ss}`;
}
